import TandemLaunch from '../../../features/tandem/TandemLaunch';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Empty,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons';

import { useModal } from 'services';
import {
  fetchBlueprint,
  publishBlueprintVersion,
  setVariantVisibility,
  validateVariant,
  selectors,
  BLUEPRINT_PUBLISHED,
  BLUEPRINT_STATUSES,
  BlueprintDifficulty,
  BlueprintVersion,
  BlueprintVariant,
} from 'state';
import { retryAssessmentGeneration } from './api';
import { nextStepFor, NextStep } from './nextStep';
import {
  LoadFailure,
  AnswerKeyGateTag,
  NO_ANSWER_KEY_CHIP,
  hasAnswerKey,
  hasCard,
  readAnswerKeyGate,
  readCoverage,
} from 'components';
import AddVersionModal from './AddVersionModal';
import IntroduceDifficultyModal from './IntroduceDifficultyModal';
import AnswerKeyPanel from './AnswerKeyPanel';
import AnswerKeyDetailsDrawer from './AnswerKeyDetailsDrawer';
import CoverageTable from './CoverageTable';
import useAnswerKeyCoverage from './useAnswerKeyCoverage';
import useBlueprintPreparation from '../../TrainerDashboard/useBlueprintPreparation';
import './blueprintAnswerKey.css';

/**
 * THE BLUEPRINT PAGE IS THE FRONT DOOR (D1).
 *
 * This page used to be a grid of versions and difficulties with an Edit
 * button. Everything about whether a scenario could actually be MARKED lived
 * elsewhere: whether an answer key existed at all, whether it was derived
 * from what this version holds now, whether it passed its quality audit, and
 * which of the instruments a class can trade it grades. An admin could
 * publish a version, hand it to a trainer, and find out at launch.
 *
 * So the grid gained two chips per variant -- the audit verdict and how much
 * of the variant is graded -- and the page gained the answer key itself: the
 * panel that derives it, the per-instrument table that says what it marks,
 * and the drawer holding the evidence. One variant is selected at a time,
 * through `?variant=`, so a link to this page is a link to what somebody was
 * looking at.
 */

// One grid row = a version; one extra column per difficulty holds that
// version's variant (or a blank when the version predates the difficulty).
type GridRow = BlueprintVersion;

// Where the selected variant's answer key is shown. Selecting a variant
// scrolls here, so the click has a visible effect even when the variant was
// already selected and the panel sits below the fold.
const ANSWER_KEY_ANCHOR = 'bp-answer-key-panel';

// Validate is temporarily hidden from the UI. Kept fully wired (validateVariant
// action + dispatch below) so re-enabling is a one-line flip back to `true`.
const SHOW_VALIDATE_BUTTON: boolean = false;

const BlueprintDetail: React.FC = () => {
  const dispatch = useDispatch();
  const { blueprintId } = useParams<{ blueprintId: string }>();
  const id = Number(blueprintId);

  const blueprints = useSelector(selectors.blueprints);
  const waiting = useSelector(selectors.blueprintsWaiting);
  const catalogueError = useSelector(selectors.blueprintsError);
  const blueprint = blueprints[id];

  const [AddVersion, showAddVersion] = useModal(AddVersionModal);
  const [IntroduceDiff, showIntroduceDiff] = useModal(IntroduceDifficultyModal);

  const history = useHistory();
  const { search } = useLocation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Bumped by a derivation that succeeded and by an activation, so the page
  // re-reads what it just changed rather than showing the state before it.
  const [reloadToken, setReloadToken] = useState(0);
  useEffect(() => {
    // The list intentionally omits assessment work. Refresh this family on
    // every visit, including navigation from a previously loaded compact list.
    dispatch(fetchBlueprint({ id, summary_scope: 'none' }));
  }, [dispatch, id]);

  const knownVariantIds = useMemo(
    () =>
      new Set(
        (blueprint?.versions || []).flatMap(version =>
          version.variants.map(variant => variant.id),
        ),
      ),
    [blueprint],
  );

  /**
   * The variant the page is about. A `?variant=` that names one of this
   * family's own variants wins -- that is what a shared link is for -- and
   * anything else falls back to what an admin opening the page cold would
   * want: the newest authored version, including a draft awaiting release.
   */
  const defaultVariantId = useMemo(() => {
    if (!blueprint) return undefined;
    const byNewest = [...blueprint.versions].sort(
      (a, b) => b.version_number - a.version_number,
    );
    for (const version of byNewest) {
      const visible = version.variants.find(v => v.visible_to_trainers);
      if (visible) return visible.id;
      if (version.variants.length) return version.variants[0].id;
    }
    return undefined;
  }, [blueprint]);

  const requested = Number(new URLSearchParams(search).get('variant'));
  const selectedVariantId =
    requested && knownVariantIds.has(requested) ? requested : defaultVariantId;

  const selectedVersion = useMemo(
    () =>
      (blueprint?.versions || []).find(version =>
        version.variants.some(variant => variant.id === selectedVariantId),
      ),
    [blueprint, selectedVariantId],
  );
  const selectedVariant = useMemo(
    () =>
      (selectedVersion?.variants || []).find(
        variant => variant.id === selectedVariantId,
      ),
    [selectedVersion, selectedVariantId],
  );

  const latestVersionNumber = Math.max(
    0,
    ...(blueprint?.versions || []).map(version => version.version_number),
  );
  const automaticallyPrepare = Boolean(
    selectedVersion &&
      selectedVersion.version_number === latestVersionNumber &&
      selectedVersion.status === BLUEPRINT_PUBLISHED,
  );
  const preparation = useBlueprintPreparation({
    blueprintId: id,
    variantId: selectedVariantId,
    enabled: automaticallyPrepare,
    revision: `${blueprint?.updated || ''}:${reloadToken}`,
    refreshToken: blueprint,
  });

  useEffect(() => {
    // Draft and historical versions stay read-only when inspected.
    if (!selectedVersion || !selectedVariantId || automaticallyPrepare) return;
    dispatch(
      fetchBlueprint({
        id,
        summary_scope: 'latest',
        summary_variant_id: selectedVariantId,
      }),
    );
  }, [
    dispatch,
    id,
    selectedVariantId,
    selectedVersion?.version_number,
    automaticallyPrepare,
  ]);

  const selectVariant = useCallback(
    (variantId: number) => {
      const params = new URLSearchParams(search);
      params.set('variant', String(variantId));
      history.replace({ search: `?${params.toString()}` });
      const panel = document.getElementById(ANSWER_KEY_ANCHOR);
      if (panel && typeof panel.scrollIntoView === 'function') {
        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [history, search],
  );

  const runCellStep = useCallback(
    async (variantId: number, step: NextStep) => {
      if (blueprint && (step.kind === 'derive' || step.kind === 'regenerate')) {
        try {
          await retryAssessmentGeneration(blueprint.id, variantId);
          dispatch(
            fetchBlueprint({
              id,
              summary_scope: 'latest',
              summary_variant_id: variantId,
            }),
          );
          setReloadToken(token => token + 1);
        } catch {
          // Not retryable through the automatic path (a hand-derived family,
          // or a job already running): the panel below holds the manual
          // control, and the selection takes the admin there.
        }
      }
      selectVariant(variantId);
    },
    [blueprint, dispatch, id, selectVariant],
  );

  // Socket updates and explicit actions refresh immediately; pending automatic
  // regeneration is also polled so a missed socket event cannot strand the UI.
  const coverageRefreshToken = useMemo(
    () => ({ blueprint, reloadToken }),
    [blueprint, reloadToken],
  );
  const {
    document: coverage,
    loading: readingCoverage,
    error: coverageError,
    retrying,
    retry,
  } = useAnswerKeyCoverage({
    blueprintId: id,
    variantId: selectedVariantId,
    refreshToken: coverageRefreshToken,
    enabled: !automaticallyPrepare || Boolean(preparation.summary),
  });
  const coverageLoading = preparation.preparing || readingCoverage;

  const keyGate = useMemo(
    () => readAnswerKeyGate(coverage?.key || undefined),
    [coverage],
  );

  if (catalogueError) {
    return (
      <LoadFailure
        subject="Scenarios"
        onRetry={() => dispatch(fetchBlueprint({ id, summary_scope: 'none' }))}
      />
    );
  }

  if (!blueprint) {
    return (
      <div>
        <Link to="/dashboard/blueprints">← Back to blueprints</Link>
        <Empty
          style={{ marginTop: 40 }}
          description={waiting ? 'Loading…' : 'Blueprint not found'}
        />
      </div>
    );
  }

  const difficulties: BlueprintDifficulty[] = [...blueprint.difficulties].sort(
    (a, b) => a.level - b.level,
  );

  const variantCell = (
    version: BlueprintVersion,
    difficulty: BlueprintDifficulty,
  ) => {
    const variant: BlueprintVariant | undefined = version.variants.find(
      v => v.difficulty.id === difficulty.id,
    );
    if (!variant) {
      // Not a broken cell: this version has no variant at this difficulty
      // (it predates the difficulty, or was imported without one).
      return (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          No {difficulty.label} variant on this version
        </Typography.Text>
      );
    }
    // Read off the card the list endpoint already served, so a cell costs no
    // request. A card that never carried the facts (the socket broadcast
    // deliberately omits them) draws no chips rather than drawing zeroes.
    const summary =
      variant.id === selectedVariantId && automaticallyPrepare
        ? preparation.summary
        : variant.launch_summary;
    const gate = summary?.answer_key
      ? readAnswerKeyGate({ answer_key_gate: summary.answer_key_gate })
      : null;
    /**
     * WHAT THE CHIPS MAY CLAIM (D1). A fraction counts instruments against a
     * marking scheme, so there has to BE one: an exercise with no answer key
     * had "2 / 2 graded" in green under the page's own "No answer key has
     * been derived for this version yet", which is the front door telling an
     * admin the opposite of what the trainer's launch modal tells a trainer
     * about the same variant. A card that named no key carries the count of
     * what a class will TRADE, and nothing about what is marked.
     *
     * ABSENT IS NOT EMPTY: a delivery that carried no card at all (the socket
     * broadcast) says nothing about this variant, and draws no chip either
     * way.
     */
    const named = hasAnswerKey(summary);
    const cellCoverage = named ? readCoverage(summary) : undefined;
    const keyless = hasCard(summary) && !named;
    const selected = variant.id === selectedVariantId;
    const step = nextStepFor(
      version,
      { ...variant, launch_summary: summary },
      latestVersionNumber,
      version.status === BLUEPRINT_PUBLISHED,
    );
    return (
      <Space
        direction="vertical"
        size={4}
        style={{ width: '100%' }}
        className={
          selected
            ? 'bp-variant-cell bp-variant-cell--selected'
            : 'bp-variant-cell'
        }
      >
        <Typography.Text
          type="secondary"
          style={{ fontSize: 12 }}
          copyable={{ text: String(variant.template_session) }}
        >
          Template session ID: {variant.template_session}
        </Typography.Text>
        <Link to={`/dashboard/blueprints/${blueprint.id}/author/${variant.id}`}>
          <Button size="small" block>
            {version.is_locked ? 'View config' : 'Edit config'}
          </Button>
        </Link>
        <TandemLaunch blueprintId={blueprint.id} variant={variant} published={version.status === BLUEPRINT_PUBLISHED} />
        <Button
          size="small"
          block
          icon={selected ? <CheckOutlined /> : undefined}
          aria-pressed={selected}
          aria-label={`Show the answer key for v${version.version_number} ${difficulty.label}`}
          onClick={() => selectVariant(variant.id)}
        >
          {selected ? 'Viewing answer key below' : 'View answer key below'}
        </Button>
        {(gate || cellCoverage || keyless || summary?.launchable === false) && (
          <div className="bp-variant-cell__chips">
            <AnswerKeyGateTag gate={gate} />
            {keyless && <Tag>{NO_ANSWER_KEY_CHIP}</Tag>}
            {cellCoverage && (
              // Green is "in service and complete". A variant the launch
              // refuses is neither, whatever its counts add up to.
              <Tag
                color={
                  cellCoverage.complete && summary?.launchable !== false
                    ? 'green'
                    : 'gold'
                }
              >{`${cellCoverage.graded} / ${cellCoverage.tradable} graded`}</Tag>
            )}
            {/* The launch invariant (D3) at a glance. The sentence itself is
                on the panel below and in the tooltip; the cell only has to
                say WHICH variants cannot be run. */}
            {summary?.launchable === false && (
              <Tooltip
                title={
                  summary.launch_refusal ||
                  'The launch endpoint refuses this variant.'
                }
              >
                <Tag color="red">Launch refused</Tag>
              </Tooltip>
            )}
          </div>
        )}
        {step.kind !== 'unknown' && (
          <div className="bp-variant-cell__next">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {step.hint}
            </Typography.Text>
            {step.action && (
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => runCellStep(variant.id, step)}
              >
                {step.action}
              </Button>
            )}
          </div>
        )}
        <Space size={6} align="center">
          <Tooltip title="Visible to trainers when the version is published">
            <Switch
              size="small"
              checked={variant.visible_to_trainers}
              onChange={checked =>
                dispatch(
                  setVariantVisibility({
                    id: blueprint.id,
                    variant_id: variant.id,
                    visible: checked,
                  }),
                )
              }
            />
          </Tooltip>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {variant.visible_to_trainers ? 'Visible' : 'Hidden'}
          </Typography.Text>
        </Space>
        {SHOW_VALIDATE_BUTTON && (
          <Button
            size="small"
            type="link"
            style={{ padding: 0 }}
            onClick={() =>
              dispatch(
                validateVariant({ id: blueprint.id, variant_id: variant.id }),
              )
            }
          >
            Validate
          </Button>
        )}
      </Space>
    );
  };

  const columns: ColumnsType<GridRow> = [
    {
      title: 'Version',
      key: 'version',
      fixed: 'left',
      width: 220,
      render: function VersionCol(_v, version) {
        const published = version.status === BLUEPRINT_PUBLISHED;
        const latest = version.version_number === latestVersionNumber;
        const frozenLatest =
          latest &&
          version.is_locked &&
          !version.variants.some(
            variant => variant.launch_summary?.launchable === true,
          );
        return (
          <Space direction="vertical" size={4}>
            <Space size={6}>
              <Typography.Text strong>
                v{version.version_number}
              </Typography.Text>
              <Tag color={published ? 'green' : 'default'}>
                {BLUEPRINT_STATUSES[version.status]}
              </Tag>
              {version.is_locked && <Tag color="red">Locked</Tag>}
            </Space>
            {version.changelog && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {version.changelog}
              </Typography.Text>
            )}
            <Space size={6}>
              {!published && (
                <Button
                  size="small"
                  type="primary"
                  onClick={() =>
                    dispatch(
                      publishBlueprintVersion({
                        id: blueprint.id,
                        version_id: version.id,
                      }),
                    )
                  }
                >
                  Publish
                </Button>
              )}
              {!version.is_locked && (
                <Button
                  size="small"
                  onClick={() =>
                    showIntroduceDiff({
                      blueprint,
                      versionId: version.id,
                    })
                  }
                >
                  Add difficulty
                </Button>
              )}
              {latest && (
                <Tooltip
                  title={
                    frozenLatest
                      ? `v${version.version_number} was launched, so its exercise is frozen. Changes and a new answer key go into v${version.version_number + 1}.`
                      : 'Copies this version into a new draft version.'
                  }
                >
                  <Button
                    size="small"
                    type={frozenLatest ? 'primary' : 'default'}
                    icon={<PlusOutlined />}
                    onClick={() =>
                      showAddVersion({ blueprintId: blueprint.id })
                    }
                  >
                    New version from v{version.version_number}
                  </Button>
                </Tooltip>
              )}
            </Space>
          </Space>
        );
      },
    },
    ...difficulties.map<ColumnsType<GridRow>[number]>(difficulty => ({
      title: `${difficulty.label} (lvl ${difficulty.level})`,
      key: `difficulty-${difficulty.id}`,
      width: 200,
      render: (_v, version) => variantCell(version, difficulty),
    })),
  ];

  const versions: GridRow[] = [...blueprint.versions].sort(
    (a, b) => b.version_number - a.version_number,
  );

  const keyStatus = String(coverage?.key?.status || '').toLowerCase();
  const publishedWithoutALiveKey =
    !coverageLoading &&
    Boolean(coverage) &&
    selectedVersion?.status === BLUEPRINT_PUBLISHED &&
    (!coverage?.key || keyStatus === 'draft');

  return (
    <div>
      <Space
        style={{
          marginBottom: 20,
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Space direction="vertical" size={2}>
          <Link to="/dashboard/blueprints">← Back to blueprints</Link>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {blueprint.name}
          </Typography.Title>
          {blueprint.category && (
            <Typography.Text type="secondary">
              {blueprint.category}
            </Typography.Text>
          )}
        </Space>
      </Space>
      <Table
        className="bp-version-grid"
        rowKey="id"
        loading={waiting}
        dataSource={versions}
        columns={columns}
        bordered
        pagination={false}
        scroll={{ x: 'max-content' }}
      />

      {/* Publishing a version whose key is missing or still a draft is
          allowed -- the hard stop is the launch invariant (D3), not the
          publish -- but it is said out loud here, because a published
          version is what a trainer picks from. */}
      {publishedWithoutALiveKey && !coverage?.regeneration?.automatic && (
        <Alert
          className="assessment-callout"
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          title="This version is published and its answer key is not in service"
          description={
            coverage?.key
              ? 'The key for this version is a draft: it did not pass its quality audit, so a launch of this variant will be refused until it does or an administrator signs for it.'
              : 'No answer key has been derived for this version, so a session launched from it is not graded at all.'
          }
        />
      )}

      <div id={ANSWER_KEY_ANCHOR}>
        {preparation.preparing ? (
          <Alert
            style={{ marginTop: 16 }}
            type="info"
            showIcon
            title="Preparing assessment"
            description="We are checking this version and updating its answer key where needed. This will refresh automatically when it is ready."
          />
        ) : preparation.error ? (
          <Alert
            style={{ marginTop: 16 }}
            type="error"
            showIcon
            title="Assessment could not be checked"
            description={preparation.error}
            action={<Button onClick={preparation.retry}>Retry</Button>}
          />
        ) : coverageError && !coverage ? (
          <LoadFailure
            subject="The answer key"
            onRetry={() => setReloadToken(token => token + 1)}
          />
        ) : (
          <AnswerKeyPanel
            blueprintId={blueprint.id}
            variantId={selectedVariantId}
            versionNumber={selectedVersion?.version_number}
            templateSessionId={selectedVariant?.template_session}
            document={coverage}
            loading={coverageLoading}
            error={coverageError}
            retrying={retrying}
            onRetry={retry}
            onRegenerated={() => setReloadToken(token => token + 1)}
            onOpenDetails={() => setDetailsOpen(true)}
            versionLocked={Boolean(selectedVersion?.is_locked)}
            onNewVersion={() => showAddVersion({ blueprintId: blueprint.id })}
          />
        )}
      </div>

      {/* The panel above already states the counts; the table lists the rows. */}
      <CoverageTable
        document={coverage}
        loading={coverageLoading}
        showCounts={false}
      />

      <AnswerKeyDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        blueprintId={blueprint.id}
        blueprintName={blueprint.name}
        variantId={selectedVariantId}
        answerKeyCode={coverage?.key?.code || null}
        answerKeyName={coverage?.key?.name || null}
        auditFailed={
          keyGate?.state === 'blocked' || keyGate?.state === 'overridden'
        }
        launchBlocked={Boolean(coverage?.key?.launch_blocked)}
        launchBlockReason={coverage?.key?.launch_block_reason || ''}
        onChanged={() => setReloadToken(token => token + 1)}
      />

      {AddVersion}
      {IntroduceDiff}
    </div>
  );
};

export default BlueprintDetail;
